import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';

const sampleMeetings = [
  {
    id: 'MTG-001',
    title: 'Q2 Deal Review',
    date: '2026-06-10',
    time: '10:00 AM',
    participants: ['Ava Wilson', 'Noah Bennett', 'Sarah Johnson'],
    relatedOpportunity: 'CRM Rollout Q3',
    agenda: 'Review commercial scope, blockers, and next stage approval.',
    notes: 'Customer requested a revised onboarding timeline and executive summary.',
    outcomes: 'Approved for updated proposal submission by Friday.',
    followUpTasks: ['Send revised onboarding plan', 'Confirm legal review date'],
  },
  {
    id: 'MTG-002',
    title: 'Implementation Planning Call',
    date: '2026-06-12',
    time: '02:30 PM',
    participants: ['Emma Reed', 'Michael Reed'],
    relatedOpportunity: 'Compliance Workflow Program',
    agenda: 'Align implementation milestones and security checklist.',
    notes: 'Need compliance owner signoff before phase 1 start.',
    outcomes: 'Workshop scheduled with compliance team next week.',
    followUpTasks: ['Share compliance checklist', 'Book architecture workshop'],
  },
  {
    id: 'MTG-003',
    title: 'Pricing Negotiation Session',
    date: '2026-06-15',
    time: '11:15 AM',
    participants: ['Olivia Brooks', 'Daniel Moore'],
    relatedOpportunity: 'Warehouse Automation Suite',
    agenda: 'Discuss pricing options, discounts, and support tiers.',
    notes: 'Customer is price-sensitive but interested in premium support.',
    outcomes: 'Finance to prepare revised pricing matrix.',
    followUpTasks: ['Build discount comparison', 'Email final commercial options'],
  },
  {
    id: 'MTG-004',
    title: 'Stakeholder Introduction',
    date: '2026-06-18',
    time: '04:00 PM',
    participants: ['Liam Carter', 'Ryan Patel', 'Emily Carter'],
    relatedOpportunity: 'Field Ops Dashboard',
    agenda: 'Introduce delivery team and review account goals.',
    notes: 'Stakeholders want weekly executive updates.',
    outcomes: 'Created shared cadence for weekly progress sync.',
    followUpTasks: ['Draft weekly update template'],
  },
];

const MeetingDetailDrawer = ({ meeting, open, onClose }) => {
  if (!meeting) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 560 }, maxWidth: '100%' } }}
    >
      <Box sx={{ p: 2.25, color: '#fff', background: 'linear-gradient(135deg, #7f1d1d 0%, #c62828 60%, #ef5350 100%)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.25 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{meeting.title}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {meeting.date} • {meeting.time}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
          <Chip size="small" label={`${meeting.participants.length} Participants`} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }} />
          <Chip size="small" label={meeting.relatedOpportunity} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }} />
        </Stack>
      </Box>

      <Box sx={{ p: 2.5, bgcolor: '#f8fafc', flex: 1, overflowY: 'auto' }}>
        <Stack spacing={1.25}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75 }}>Agenda</Typography>
            <Typography>{meeting.agenda}</Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75 }}>Notes</Typography>
            <Typography>{meeting.notes}</Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75 }}>Outcomes</Typography>
            <Typography>{meeting.outcomes}</Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75 }}>Follow-up Tasks</Typography>
            <List disablePadding>
              {meeting.followUpTasks.map((task, index) => (
                <ListItem key={`${task}-${index}`} disableGutters sx={{ py: 0.4 }}>
                  <ListItemText primary={task} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Stack>
      </Box>
    </Drawer>
  );
};

export const Meetings = () => {
  const [view, setView] = useState('calendar');
  const [meetings, setMeetings] = useState(sampleMeetings);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedDate, setSelectedDate] = useState(dayjs('2026-06-10'));
  const [openCreate, setOpenCreate] = useState(false);
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    date: '2026-06-10',
    time: '',
    participants: '',
    relatedOpportunity: '',
    agenda: '',
    notes: '',
    outcomes: '',
    followUpTasks: '',
  });

  const filteredMeetings = useMemo(() => {
    const term = search.trim().toLowerCase();
    return meetings.filter(meeting => {
      if (!term) return true;
      return [meeting.title, meeting.relatedOpportunity, ...meeting.participants]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [meetings, search]);

  const meetingsForSelectedDate = useMemo(() => {
    const selected = selectedDate.format('YYYY-MM-DD');
    return filteredMeetings.filter(meeting => meeting.date === selected);
  }, [filteredMeetings, selectedDate]);

  const agendaMeetings = useMemo(
    () => [...filteredMeetings].sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`)),
    [filteredMeetings]
  );

  const handleCreateMeeting = () => {
    if (!newMeeting.title || !newMeeting.date || !newMeeting.time) return;

    const meeting = {
      id: `MTG-${Date.now()}`,
      title: newMeeting.title,
      date: newMeeting.date,
      time: newMeeting.time,
      participants: newMeeting.participants
        .split(',')
        .map(value => value.trim())
        .filter(Boolean),
      relatedOpportunity: newMeeting.relatedOpportunity || '-',
      agenda: newMeeting.agenda || '-',
      notes: newMeeting.notes || '-',
      outcomes: newMeeting.outcomes || '-',
      followUpTasks: newMeeting.followUpTasks
        .split(',')
        .map(value => value.trim())
        .filter(Boolean),
    };

    setMeetings(prev => [meeting, ...prev]);
    setOpenCreate(false);
    setNewMeeting({
      title: '',
      date: '2026-06-10',
      time: '',
      participants: '',
      relatedOpportunity: '',
      agenda: '',
      notes: '',
      outcomes: '',
      followUpTasks: '',
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: '#f6f8fc', minHeight: '100vh' }}>
        <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <GroupsIcon sx={{ color: '#c62828', fontSize: 30 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#7f1d1d' }}>
                  CRM Meetings
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Customer meeting planner with calendar, agenda, and detailed tracking
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setOpenCreate(true)}
              sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#c62828', '&:hover': { bgcolor: '#a91f1f' } }}
            >
              New Meeting
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search title, participants, opportunity"
              value={search}
              onChange={event => setSearch(event.target.value)}
              sx={{ minWidth: 320, maxWidth: 420, bgcolor: '#fff' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#64748b' }} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Chip
              icon={<CalendarMonthOutlinedIcon />}
              label={`${filteredMeetings.length} meetings`}
              sx={{ fontWeight: 700, bgcolor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}
            />
          </Box>

          <Paper sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, mb: 2 }}>
            <Tabs
              value={view}
              onChange={(_, value) => setView(value)}
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{ style: { display: 'none' } }}
              sx={{ p: 1, '& .MuiTabs-flexContainer': { gap: 0.8 } }}
            >
              <Tab value="calendar" label="Calendar View" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, '&.Mui-selected': { bgcolor: '#fee2e2', color: '#991b1b' } }} />
              <Tab value="agenda" label="Agenda View" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, '&.Mui-selected': { bgcolor: '#fee2e2', color: '#991b1b' } }} />
              <Tab value="list" label="List View" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, '&.Mui-selected': { bgcolor: '#fee2e2', color: '#991b1b' } }} />
            </Tabs>
          </Paper>

          {view === 'calendar' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '360px 1fr' }, gap: 2 }}>
              <Paper sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                <DateCalendar value={selectedDate} onChange={value => value && setSelectedDate(value)} />
              </Paper>

              <Paper sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                <Typography sx={{ fontWeight: 800, mb: 1.25, color: '#7f1d1d' }}>
                  Meetings on {selectedDate.format('DD MMM YYYY')}
                </Typography>

                <Stack spacing={1}>
                  {meetingsForSelectedDate.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="body2" color="text.secondary">No meetings scheduled for this date.</Typography>
                    </Paper>
                  ) : (
                    meetingsForSelectedDate.map(meeting => (
                      <Paper
                        key={meeting.id}
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: 2.5, cursor: 'pointer' }}
                        onClick={() => setSelectedMeeting(meeting)}
                      >
                        <Typography sx={{ fontWeight: 800 }}>{meeting.title}</Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.8, flexWrap: 'wrap', rowGap: 0.8 }}>
                          <Chip size="small" icon={<AccessTimeOutlinedIcon />} label={meeting.time} />
                          <Chip size="small" icon={<PersonOutlineIcon />} label={`${meeting.participants.length} Participants`} />
                        </Stack>
                        <Typography variant="body2" sx={{ mt: 0.8, color: '#64748b' }}>{meeting.relatedOpportunity}</Typography>
                      </Paper>
                    ))
                  )}
                </Stack>
              </Paper>
            </Box>
          )}

          {view === 'agenda' && (
            <Stack spacing={1}>
              {agendaMeetings.map(meeting => (
                <Paper
                  key={meeting.id}
                  variant="outlined"
                  sx={{ p: 1.6, borderRadius: 2.5, cursor: 'pointer' }}
                  onClick={() => setSelectedMeeting(meeting)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>{meeting.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {meeting.date} • {meeting.time}
                      </Typography>
                    </Box>
                    <Chip label={meeting.relatedOpportunity} size="small" sx={{ fontWeight: 700, bgcolor: '#fef2f2', color: '#b91c1c' }} />
                  </Box>

                  <Typography variant="body2" sx={{ mt: 1 }}>{meeting.agenda}</Typography>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#64748b' }}>
                    Participants: {meeting.participants.join(', ')}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          )}

          {view === 'list' && (
            <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, bgcolor: '#fff' }}>
              <Table sx={{ minWidth: 980 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Participants</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Related Opportunity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMeetings.map(meeting => (
                    <TableRow key={meeting.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedMeeting(meeting)}>
                      <TableCell sx={{ fontWeight: 700 }}>{meeting.title}</TableCell>
                      <TableCell>{meeting.date}</TableCell>
                      <TableCell>{meeting.time}</TableCell>
                      <TableCell>{meeting.participants.join(', ')}</TableCell>
                      <TableCell>{meeting.relatedOpportunity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <MeetingDetailDrawer meeting={selectedMeeting} open={Boolean(selectedMeeting)} onClose={() => setSelectedMeeting(null)} />

        <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 800 }}>New Meeting</DialogTitle>
          <DialogContent>
            <Stack spacing={1.2} sx={{ mt: 1 }}>
              <TextField label="Title" value={newMeeting.title} onChange={event => setNewMeeting(prev => ({ ...prev, title: event.target.value }))} fullWidth />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.2 }}>
                <TextField
                  label="Date"
                  type="date"
                  value={newMeeting.date}
                  onChange={event => setNewMeeting(prev => ({ ...prev, date: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField label="Time" value={newMeeting.time} onChange={event => setNewMeeting(prev => ({ ...prev, time: event.target.value }))} fullWidth />
              </Box>
              <TextField label="Participants" value={newMeeting.participants} onChange={event => setNewMeeting(prev => ({ ...prev, participants: event.target.value }))} helperText="Comma separated" fullWidth />
              <TextField
                label="Related Opportunity"
                value={newMeeting.relatedOpportunity}
                onChange={event => setNewMeeting(prev => ({ ...prev, relatedOpportunity: event.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkOutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
              <TextField label="Agenda" value={newMeeting.agenda} onChange={event => setNewMeeting(prev => ({ ...prev, agenda: event.target.value }))} multiline rows={2} fullWidth />
              <TextField label="Notes" value={newMeeting.notes} onChange={event => setNewMeeting(prev => ({ ...prev, notes: event.target.value }))} multiline rows={2} fullWidth />
              <TextField label="Outcomes" value={newMeeting.outcomes} onChange={event => setNewMeeting(prev => ({ ...prev, outcomes: event.target.value }))} multiline rows={2} fullWidth />
              <TextField label="Follow-up Tasks" value={newMeeting.followUpTasks} onChange={event => setNewMeeting(prev => ({ ...prev, followUpTasks: event.target.value }))} helperText="Comma separated" fullWidth />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreate(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>Cancel</Button>
            <Button onClick={handleCreateMeeting} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#c62828', '&:hover': { bgcolor: '#a91f1f' } }}>
              Create
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Meetings;
